import React, { useState, useMemo } from 'react';
import * as Yup from 'yup';
import { Alert, Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import allForm from '../../Stateless/Form';
import { COLORS } from '../../../theme/themeConstants';
import { MultiSelectField } from './MultiSelectField';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useAllMerchant } from '../../Query/MerchantQuery';
import { useReportApi } from '../../../hooks/useReportApi';

const validationSchema = Yup.object().shape({
  fromDate: Yup.string().required('From Date is required'),
  toDate: Yup.string().required('To Date is required'),
  storeType: Yup.string().nullable(),
  store: Yup.array().nullable(),
  storeOwner: Yup.array().nullable(),
});

const RejectedOrdersReport: React.FC = () => {
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | undefined>(undefined);

  const { downloadCancelledOrdersReport, loading } = useReportApi();

  // Fetch data from SDK
  const { data: storesData } = useAllStores({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    statusFilter: 'All',
    addedByFilter: 'All',
    merchantId: selectedMerchantId,
  });

  const { data: merchantData } = useAllMerchant({ initialData: {} });

  const storeTypeOptions = [
    { value: '', label: 'All' },
    { value: 'Restaurant', label: 'Restaurant' },
    { value: 'GroceryStore', label: 'Grocery' },
    { value: 'PharmacyStore', label: 'Pharmacy' },
  ];

  const storeOptions = useMemo(
    () =>
      (storesData?.data || []).map((store: any) => ({
        value: store.id,
        label:
          store.restaurantDetails?.storeName ||
          store.nameEnglish ||
          store.name ||
          'Unknown Store',
        merchantId: store.merchantId,
      })),
    [storesData]
  );

  const storeOwnerOptions = useMemo(
    () =>
      (merchantData ?? []).map((merchant: any) => ({
        value: merchant.id,
        label:
          `${merchant.firstName ?? ''} ${merchant.lastName ?? ''}`.trim() ||
          merchant.name ||
          'Unknown Owner',
      })),
    [merchantData]
  );

  const formConfig = {
    initialValues: {
      fromDate: '',
      toDate: '',
      storeType: '',
      store: [],
      storeOwner: [],
    },
    validationSchema,
    onSubmit: async (values: Record<string, any>) => {
      try {
        setSuccessMessage(null);
        setErrorMessage(null);
        setInfoMessage(null);

        const { storeType, store, storeOwner } = values;

        const fromDate = selectedFromDate ? selectedFromDate?.toISOString().split('T')[0] + 'T00:00:00Z' : undefined;
        const toDate = selectedToDate ? selectedToDate?.toISOString().split('T')[0] + 'T23:59:59Z' : undefined;

        if (!fromDate || !toDate) {
          setErrorMessage('Both From Date and To Date are required');
          return;
        }

        // This report is powered by the Cancelled Orders Report endpoint,
        // restricted to merchant-denied (rejected) orders.
        const request: any = {
          dateRange: 'custom',
          dateFrom: fromDate,
          dateTo: toDate,
          orderStates: ['MERCHANT_DENIED'],
        };

        if (Array.isArray(store) && store.length > 0) {
          request.storeIds = store;
        }

        if (Array.isArray(storeOwner) && storeOwner.length > 0) {
          request.merchantId = storeOwner[0];
      }

        if (storeType) {
          request.storeType = [storeType];
        }

        const result = await downloadCancelledOrdersReport(request);

        if ((result as any)?.noData) {
          setInfoMessage(
            (result as any).message || 'No rejected orders found for the selected filters.'
          );
        } else {
          setSuccessMessage('Rejected Orders report download started.');
        }
      } catch (err: any) {
        setErrorMessage(err?.message || 'Failed to download Rejected Orders report.');
      }
    },
    validateOnChange: true,
    enableReinitialize: true,
    fields: [
      {
        name: 'fromDate',
        label: 'From Date:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedFromDate}
              onChange={(newValue: Dayjs | null) => {
                setSelectedFromDate(newValue);
                form.setFieldValue(field.name, newValue ? newValue.toISOString() : '');
              }}
              slotProps={{
                textField: {
                  placeholder: 'MM/DD/YYYY',
                  sx: { width: '300px' },
                },
              }}
            />
          </LocalizationProvider>
        ),
      },
      {
        name: 'toDate',
        label: 'To Date:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedToDate}
              onChange={(newValue: Dayjs | null) => {
                setSelectedToDate(newValue);
                form.setFieldValue(field.name, newValue ? newValue.toISOString() : '');
              }}
              slotProps={{
                textField: {
                  placeholder: 'MM/DD/YYYY',
                  sx: { width: '300px' },
                },
              }}
            />
          </LocalizationProvider>
        ),
      },
      {
        name: 'storeType',
        label: 'Store Type:',
        type: 'select',
        options: storeTypeOptions,
      },
      // Store Owner (first)
      {
        name: 'storeOwner',
        label: 'Store Owner:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <MultiSelectField
            field={field}
            form={form}
            options={storeOwnerOptions}
            placeholder="Select Store Owners"
            width="300px"
            onChangeCallback={(selectedValues: string[]) => {
              form.setFieldValue(field.name, selectedValues, true);
              setSelectedMerchantId(selectedValues[0] ?? undefined);
            }}
          />
        ),
      },
      // Stores (driven by owner-based API)
      {
        name: 'store',
        label: 'Stores:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <MultiSelectField
            field={field}
            form={form}
            options={storeOptions}
            placeholder="Select Stores"
            width="300px"
            disabled={!Array.isArray(form.values.storeOwner) || form.values.storeOwner.length === 0}
          />
        ),
      },
    ],
    customButton: () => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: COLORS.primary.main,
            textTransform: 'none',
            '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) },
          }}
        >
          {loading ? 'Downloading...' : 'Download'}
        </Button>
        <Button type="reset" variant="outlined" disabled={loading}>
          Reset
        </Button>
      </Box>
    ),
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', padding: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, marginBottom: 3, color: '#333', textAlign: 'left' }}
      >
        {'Rejected Orders Report'}
      </Typography>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: 900,
          margin: '0 auto',
        }}
      >
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {infoMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {infoMessage}
          </Alert>
        )}
        {allForm(formConfig)()}
      </Box>
    </Box>
  );
};

export default RejectedOrdersReport;
